<!-- eslint-disable max-len -->
<template>
  <u-dashboard-toolbar />
  <u-dashboard-panel-content ref="dashboardPanelContent" class="gap-4">
    <template v-if="count > 0">
      <div
        v-for="[paymentId, p] in Array.from(payments.entries()).sort((a, b) =>
          useCollator().compare(b[1].created_at, a[1].created_at),
        )"
        :key="paymentId"
        :class="{
          grid: true,
          'gap-4': true,
          border: true,
          'border-gray-300 dark:border-gray-700': true,
          'rounded-md': true,
          'w-full': true,
          'box-border': true,
        }"
      >
        <div
          v-for="(event, paymentEventKey) in p.events"
          :key="paymentEventKey"
          class="grid"
        >
          <u-button
            v-if="typeof event.id === 'string'"
            variant="link"
            :to="`/event/${useShortId(event.id)}`"
            class="w-fit p-0 pl-4 pt-4"
          >
            <h5>{{ event.name }}</h5>
          </u-button>
          <h5 v-else>
            {{ event.name }}
          </h5>
          <template v-if="p.metadata?.code === 0">
            <hr class="my-2 mt-4 dark:border-gray-700" />
            <h6 class="mt-2 px-4">Event Hosting Fee</h6>
            <app-detail
              v-if="
                typeof p.metadata.remaining_balance === 'number' &&
                p.metadata.remaining_balance > 0
              "
              class="mt-2 px-4"
              label="Remaining Balance Due"
              :value="useMoneyFormat(p.metadata.remaining_balance / 100)"
            />
            <app-detail
              v-else
              class="mt-2 px-4"
              label="Remaining Balance Due"
              value="$0.00 / Paid in full"
            />
          </template>
          <template v-else>
            <u-accordion
              class="mt-4"
              multiple
              :items="[
                ...(Object.keys(event.shows).length
                  ? [
                      {
                        label: 'Entries',
                        total: Object.values(event.shows).reduce(
                          (acc, curr) => {
                            acc += Object.values(
                              curr.payments_exhibitors,
                            ).reduce(
                              (acc, curr) =>
                                acc + curr.payments_show_objects.length,
                              0,
                            )

                            return acc
                          },
                          0,
                        ),
                      },
                    ]
                  : []),
                ...(Object.values(event.payments_exhibitors).some(
                  ({ payments_extras }) => payments_extras.length > 0,
                )
                  ? [
                      {
                        label: 'Extras',
                        total: Object.values(event.payments_exhibitors).reduce(
                          (acc, curr) => {
                            acc += curr.payments_extras.length

                            return acc
                          },
                          0,
                        ),
                      },
                    ]
                  : []),
              ]"
              :ui="{ item: { padding: 'pb-0' } }"
            >
              <template #default="{ item, open }">
                <u-button
                  color="gray"
                  variant="ghost"
                  :class="{
                    'border-t':
                      item.label !== 'Extras' ||
                      Object.keys(event.shows).length === 0,
                    'border-b': !open,
                    'border-gray-300 dark:border-gray-700': true,
                    'pointer-events-none': item.disabled,
                  }"
                  :ui="{ rounded: 'rounded-none', padding: { sm: 'p-3' } }"
                >
                  <strong class="truncate"
                    >{{ item.total }}
                    {{
                      item.total === 1
                        ? item.label === 'Entries'
                          ? 'Entry'
                          : 'Extra'
                        : item.label
                    }}</strong
                  >
                  <template #trailing>
                    <u-icon
                      name="i-ph-caret-right"
                      class="ms-auto transition-transform duration-200"
                      :class="[open && 'rotate-90']"
                    />
                  </template>
                </u-button>
              </template>
              <template #item="{ item }">
                <div
                  v-if="item.label === 'Entries'"
                  class="border-b border-gray-300 pb-2 dark:border-gray-700"
                >
                  <div
                    v-for="(show, paymentShowKey) in event.shows"
                    :key="paymentShowKey"
                    class="mt-4 box-border grid w-full gap-2 px-4 first-of-type:mt-0"
                  >
                    <strong class="underline">
                      {{ show.name }}
                    </strong>
                    <div
                      v-for="(
                        showPaymentsExhibitors, showPaymentsExhibitorsKey
                      ) in show.payments_exhibitors"
                      :key="showPaymentsExhibitorsKey"
                      class="pl-2"
                    >
                      <strong class="text-sm leading-loose">
                        {{
                          showPaymentsExhibitors.exhibitor.metadata.name_first
                        }}
                        {{
                          showPaymentsExhibitors.exhibitor.metadata.name_last
                        }}
                      </strong>
                      <app-detail
                        v-for="(value, key) in showPaymentsExhibitors.exhibitor
                          .metadata.info"
                        :key="key"
                        :label="key"
                        :value="value"
                      />
                      <div class="grid gap-2">
                        <div
                          v-for="showPaymentsShowObjects in showPaymentsExhibitors.payments_show_objects"
                          :key="showPaymentsShowObjects.id"
                        >
                          <app-detail
                            v-for="[key, value] in Object.entries(
                              showPaymentsShowObjects.show_object.metadata,
                            ).sort(([keyA], [keyB]) =>
                              useEntryDisplaySort(keyA, keyB),
                            )"
                            :key="key"
                            :label="key"
                            :value="value"
                          />
                          <u-button
                            v-if="
                              event.metadata.is_exhibitor_edit_enabled &&
                              showPaymentsShowObjects.show_object.metadata
                                .division !== 'Showmanship' &&
                              (app.data.time.isBefore(
                                event.registration_end_at,
                              ) ||
                                app.data.time.isSame(event.registration_end_at))
                            "
                            variant="link"
                            class="p-0"
                            @click="
                              onEdit(
                                event,
                                show,
                                showPaymentsExhibitors.exhibitor,
                                showPaymentsShowObjects.show_object,
                              )
                            "
                          >
                            Edit
                          </u-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="item.label === 'Extras'"
                  class="border-b border-gray-300 pb-2 dark:border-gray-700"
                >
                  <div
                    v-for="(
                      eventPaymentsExhibitors, key
                    ) in event.payments_exhibitors"
                    :key="key"
                    class="px-4"
                  >
                    <div
                      v-for="exhibitorPaymentsExtras in eventPaymentsExhibitors.payments_extras"
                      :key="exhibitorPaymentsExtras.id"
                      class="mt-2 box-border grid w-full gap-2 first-of-type:mt-0"
                    >
                      <strong class="underline">
                        {{ exhibitorPaymentsExtras.extra.name }}
                      </strong>
                      <div class="pl-2">
                        <strong class="text-sm leading-loose">
                          {{
                            eventPaymentsExhibitors.exhibitor.metadata
                              .name_first
                          }}
                          {{
                            eventPaymentsExhibitors.exhibitor.metadata.name_last
                          }}
                        </strong>
                        <app-detail
                          label="Quantity"
                          :value="exhibitorPaymentsExtras.quantity"
                        />
                        <app-detail
                          v-for="(value, variant) in exhibitorPaymentsExtras
                            .metadata.variants"
                          :key="`${eventPaymentsExhibitors.exhibitor.id}.${exhibitorPaymentsExtras.extra.id}.${variant}`"
                          :label="variant"
                          :value="value"
                        />
                        <app-detail
                          v-for="(value, question) in exhibitorPaymentsExtras
                            .metadata.info"
                          :key="`${eventPaymentsExhibitors.exhibitor.id}.${exhibitorPaymentsExtras.extra.id}.${question}`"
                          :label="question"
                          :value="value"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </u-accordion>
          </template>
        </div>
        <div class="grid grid-cols-[1fr_auto] items-end px-4 pb-4">
          <div>
            <strong class="text-sm text-gray-500 dark:text-gray-400">
              {{ useMoneyFormat(p.amount) }}
            </strong>
            <p
              :class="{
                flex: true,
                'items-center': true,
                'gap-1': true,
                'text-sm': true,
                'w-fit': true,
                'text-gray-500 dark:text-gray-400': true,
                'cursor-pointer': isSupported,
              }"
              @click="onTrackingIdClick(useShortId(p.id))"
            >
              {{ useShortId(p.id) }}
              <u-icon v-if="isSupported" title="Copy" name="i-ph-clipboard" />
            </p>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ useTime(p.created_at).format('MM/DD/YYYY hh:mm:ss A') }}
          </p>
        </div>
      </div>
      <div v-if="payments.size" class="grid w-full justify-center gap-2">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ payments.size }} / {{ count }} Payments Shown
        </p>
        <u-button
          v-if="count > payments.size"
          variant="link"
          class="p-0"
          @click="loadPayments"
        >
          Load More
        </u-button>
      </div>
    </template>
    <div v-else class="box-border flex w-full items-center justify-center p-6">
      <p>No payments found.</p>
    </div>
  </u-dashboard-panel-content>
  <modal-edit-entry
    v-if="activeEvent && activeShow && activeSubuserId"
    v-model="isAddEntryModalActive"
    :subuser-id="activeSubuserId"
    :event="activeEvent"
    :show="activeShow"
    :exhibitor="activeExhibitor"
    :cart-entry="activeEntry"
    :loading="app.state.isLoading"
    @add-to-cart="updateEntry"
  />
</template>
<!-- eslint-enable max-len -->

<script lang="ts" setup>
import type { SchemaUiContentful } from '@tarcltd/form-vue'

type PortalPaymentsDatum = PortalPayment & {
  events: Record<
    /**
     * payment_id.event_id
     */
    string,
    PortalEvent & {
      shows: Record<
        /**
         * payment_id.show_id
         */
        string,
        PortalShow & {
          payments_exhibitors: Record<
            /**
             * event_id.show_id.exhibitor_id
             */
            string,
            PortalPaymentExhibitor & {
              payments_show_objects: (PortalPaymentShowObject & {
                show_object: PortalShowObject
              })[]
              exhibitor: PortalExhibitor
            }
          >
        }
      >
      payments_exhibitors: Record<
        /**
         * event_id.exhibitor_id
         */
        string,
        PortalPaymentExhibitor & {
          exhibitor: PortalExhibitor
          payments_extras: (PortalPaymentExtra & {
            extra: PortalExtra
          })[]
        }
      >
    }
  >
}

type PortalPaymentsData = Map<string, PortalPaymentsDatum>

definePageMeta({
  middleware: ['auth'],
})

useHead({
  title: 'The Show Portal',
})

const supabase = useSupabaseClient<Supabase>()
const auth = useAuthStore()
const app = useApp()
const data = useData()
const toast = useToast()
const dashboardPanelContent = ref<HTMLElement | null>(null)
const payments = ref<PortalPaymentsData>(new Map<string, PortalPaymentsDatum>())
const activeSubuserId = ref<string>()
const activeEvent = ref<PortalEvent>()
const activeShow = ref<PortalShow>()
const activeExhibitor = ref<PortalExhibitor>()
const activeEntry = ref<PortalCartShowObject>()
const isAddEntryModalActive = ref(false)
const { copy, isSupported } = useClipboard()
const loadRange = 10
const count = ref(0)
const startIndex = ref(0)
const endIndex = ref(startIndex.value + 50 - 1)
const debouncedFormData = useDebounceFn(formData, 50, { maxWait: 750 })

app.data.title = 'Payment History'

function incrementRange() {
  const nextStart = endIndex.value + 1

  if (count.value > nextStart) {
    startIndex.value = nextStart
    endIndex.value += loadRange
  }
}

function onTrackingIdClick(trackingId: string) {
  if (isSupported.value) {
    copy(trackingId)

    toast.add({
      color: 'green',
      title: 'Tracking ID copied to clipboard!',
    })
  }
}

function onEdit(
  event: PortalEvent,
  show: PortalShow,
  exhibitor: PortalExhibitor,
  entry: PortalShowObject,
) {
  activeSubuserId.value = entry.subuser_id
  activeEvent.value = event
  activeShow.value = show
  activeExhibitor.value = exhibitor

  activeEntry.value = {
    id: entry.id,
    type: 'show_object',
    event_id: event.id,
    show_id: show.id,
    subuser_id: entry.subuser_id,
    basis: -1,
    division: (show.manifest.properties._title as SchemaUiContentful)
      .content as string,
    exhibitor_info_submitted: exhibitor.metadata.info ?? {},
    info_submitted: entry.metadata ?? {},
  }
  isAddEntryModalActive.value = true
}

async function updateEntry(params: {
  exhibitor: PortalExhibitor
  old: PortalCartShowObject
  new: PortalCartShowObject
}) {
  try {
    isAddEntryModalActive.value = false
    app.state.isLoading = true

    if (
      !activeEvent.value?.metadata.is_exhibitor_edit_enabled ||
      app.data.time.isAfter(activeEvent.value.registration_end_at)
    ) {
      throw new Error('Editing is disabled for this event.')
    }

    const { error } = await supabase
      .from('show_objects')
      .update({
        metadata: params.new.info_submitted as SupabaseJson,
      })
      .eq('id', params.old.id)

    if (error) {
      throw error
    }

    if (
      !useDeepEqual(
        params.new.exhibitor_info_submitted,
        params.exhibitor.metadata.info,
      )
    ) {
      const { error: exhibitorError } = await supabase
        .from('exhibitors')
        .update({
          metadata: {
            ...params.exhibitor.metadata,
            info: {
              ...params.exhibitor.metadata.info,
              ...params.new.exhibitor_info_submitted,
            },
          },
        })
        .eq('id', params.exhibitor.id)

      if (exhibitorError) {
        throw exhibitorError
      }
    }

    toast.add({
      color: 'green',
      title: 'Entry updated!',
    })
  } catch (error) {
    console.error(error)

    toast.add({
      color: 'red',
      title: 'Failed to update entry.',
    })
  } finally {
    app.state.isLoading = false
    activeSubuserId.value = undefined
    activeEvent.value = undefined
    activeShow.value = undefined
    activeExhibitor.value = undefined
    activeEntry.value = undefined
  }
}

async function loadPayments() {
  let paymentsList: PortalPayment[] = []

  if (Object.keys(payments.value).length === 0) {
    const { data: initialPayments, count: paymentCount } = await supabase
      .from('payments')
      .select('*', { count: 'exact' })
      .eq('user_id', auth.userId)
      .range(startIndex.value, endIndex.value)
      .returns<PortalPayment[]>()

    incrementRange()

    if (typeof paymentCount === 'number') {
      count.value = paymentCount
    }

    if (Array.isArray(initialPayments)) {
      paymentsList = initialPayments
    }
  } else {
    const { data: nextPayments } = await supabase
      .from('payments')
      .select()
      .eq('user_id', auth.userId)
      .range(startIndex.value, endIndex.value)
      .returns<PortalPayment[]>()

    incrementRange()

    if (Array.isArray(nextPayments)) {
      paymentsList = nextPayments
    }
  }

  data.cacheMany('payments', paymentsList)

  const paymentIds = paymentsList.map((p) => p.id)
  const eventIds = paymentsList.reduce<Set<string>>((acc, curr) => {
    for (const eventId of curr.event_ids) {
      if (!data.events.has(eventId)) {
        acc.add(eventId)
      }
    }

    return acc
  }, new Set<string>())

  if (eventIds.size) {
    const { data: events } = await supabase
      .from('events')
      .select()
      .in('id', Array.from(eventIds))
      .returns<PortalEvent[]>()

    data.cacheMany('events', events)
  }

  const paymentsShowObjectsPaymentIds = new Set<string>()

  for (const [, p] of data.payments_show_objects) {
    paymentsShowObjectsPaymentIds.add(p.payment_id)
  }

  let { data: paymentsShowObjects } = await supabase
    .from('payments_show_objects')
    .select()
    .in(
      'payment_id',
      paymentIds.filter((p) => !paymentsShowObjectsPaymentIds.has(p)),
    )
    .returns<PortalPaymentShowObject[]>()

  if (!Array.isArray(paymentsShowObjects)) {
    paymentsShowObjects = []
  }

  data.cacheMany('payments_show_objects', paymentsShowObjects)

  const showObjectIds = paymentsShowObjects.reduce<Set<string>>((acc, curr) => {
    if (!data.show_objects.has(curr.show_object_id)) {
      acc.add(curr.show_object_id)
    }

    return acc
  }, new Set<string>())

  if (showObjectIds.size) {
    const { data: showObjects } = await supabase
      .from('show_objects')
      .select()
      .in('id', Array.from(showObjectIds))
      .returns<PortalShowObject[]>()

    data.cacheMany('show_objects', showObjects)
  }

  const showIds = paymentsShowObjects.reduce<Set<string>>((acc, curr) => {
    const cachedShowObject = data.show_objects.get(curr.show_object_id)

    if (cachedShowObject && !data.shows.has(cachedShowObject.show_id)) {
      acc.add(cachedShowObject.show_id)
    }

    return acc
  }, new Set<string>())

  if (showIds.size) {
    const { data: shows } = await supabase
      .from('shows')
      .select()
      .in('id', Array.from(showIds))
      .returns<PortalShow[]>()

    data.cacheMany('shows', shows)
  }

  const paymentsExhibitorsPaymentIds = new Set<string>()

  for (const [, p] of data.payments_exhibitors) {
    paymentsExhibitorsPaymentIds.add(p.payment_id)
  }

  let { data: paymentsExhibitors } = await supabase
    .from('payments_exhibitors')
    .select()
    .in(
      'payment_id',
      paymentIds.filter((p) => !paymentsExhibitorsPaymentIds.has(p)),
    )
    .returns<PortalPaymentExhibitor[]>()

  if (!Array.isArray(paymentsExhibitors)) {
    paymentsExhibitors = []
  }

  data.cacheMany('payments_exhibitors', paymentsExhibitors)

  const exhibitorIds = paymentsExhibitors
    .filter((p) => !data.exhibitors.has(p.exhibitor_id))
    .map(({ exhibitor_id }) => exhibitor_id)

  if (exhibitorIds.length) {
    const { data: exhibitors } = await supabase
      .from('exhibitors')
      .select()
      .in('id', exhibitorIds)
      .returns<PortalExhibitor[]>()

    data.cacheMany('exhibitors', exhibitors)
  }

  const paymentsExtrasPaymentIds = new Set<string>()

  for (const [, p] of data.payments_extras) {
    paymentsExtrasPaymentIds.add(p.payment_id)
  }

  let { data: paymentsExtras } = await supabase
    .from('payments_extras')
    .select()
    .in(
      'payment_id',
      paymentIds.filter((p) => !paymentsExtrasPaymentIds.has(p)),
    )
    .returns<PortalPaymentExtra[]>()

  data.cacheMany('payments_extras', paymentsExtras)

  if (!Array.isArray(paymentsExtras)) {
    paymentsExtras = []
  }

  const extraIds = paymentsExtras
    .filter((p) => !data.extras.has(p.extra_id))
    .map(({ extra_id }) => extra_id)

  if (extraIds.length) {
    const { data: extras } = await supabase
      .from('extras')
      .select()
      .in('id', extraIds)
      .returns<PortalExtra[]>()

    data.cacheMany('extras', extras)
  }
}

function formData() {
  payments.value.clear()

  for (const [, payment] of data.payments) {
    if (payment.user_id !== auth.userId) {
      continue
    }

    const newPayment: PortalPaymentsDatum = {
      ...payment,
      events: {},
    }

    for (const eventId of payment.event_ids) {
      let event = data.events.get(eventId)

      if (!event) {
        event = {
          id: eventId,
          name: 'Removed event',
        } as PortalEvent
      }

      const paymentEventKey = `${payment.id}.${eventId}`

      if (payment.metadata.code === 0) {
        newPayment.events[paymentEventKey] = {
          ...newPayment.events[paymentEventKey],
          ...event,
        }

        continue
      }

      newPayment.events[paymentEventKey] = {
        ...newPayment.events[paymentEventKey],
        ...event,
        shows: {
          ...newPayment.events[paymentEventKey]?.shows,
        },
        payments_exhibitors: {
          ...newPayment.events[paymentEventKey]?.payments_exhibitors,
        },
      }

      for (const [, paymentsExhibitors] of data.payments_exhibitors) {
        if (
          paymentsExhibitors.payment_id !== payment.id &&
          paymentsExhibitors.event_id !== eventId
        ) {
          continue
        }

        const exhibitor = data.exhibitors.get(paymentsExhibitors.exhibitor_id)

        if (!exhibitor) {
          // Extras and show objects require an exhibitor, this payment is for
          // an event hosting fee
          continue
        }

        const paymentsExtras: (PortalPaymentExtra & {
          extra: PortalExtra
        })[] = []

        for (const [, p] of data.payments_extras) {
          if (
            p.payment_id !== payment.id ||
            p.exhibitor_id !== exhibitor.id ||
            p.event_id !== eventId
          ) {
            continue
          }

          const extra = data.extras.get(p.extra_id)

          if (!extra) {
            continue
          }

          paymentsExtras.push({
            ...p,
            extra,
          })
        }

        if (paymentsExtras.length) {
          const extraPurchaseKey = `${eventId}.${exhibitor.id}`

          newPayment.events[paymentEventKey].payments_exhibitors[
            extraPurchaseKey
          ] = {
            ...newPayment.events[paymentEventKey].payments_exhibitors[
              extraPurchaseKey
            ],
            ...paymentsExhibitors,
            exhibitor,
            payments_extras: [
              ...(newPayment.events[paymentEventKey].payments_exhibitors[
                extraPurchaseKey
              ]?.payments_extras ?? []),
              ...paymentsExtras,
            ],
          }
        }

        for (const [, paymentsShowObjects] of data.payments_show_objects) {
          if (
            paymentsShowObjects.payment_id !== payment.id ||
            paymentsShowObjects.event_id !== eventId
          ) {
            continue
          }

          const showObject = data.show_objects.get(
            paymentsShowObjects.show_object_id,
          )

          if (!showObject || showObject.exhibitor_id !== exhibitor.id) {
            continue
          }

          let show = data.shows.get(showObject.show_id)

          if (!show) {
            show = {
              id: showObject.show_id,
              name: 'Removed show',
            } as PortalShow
          }

          const paymentShowKey = `${payment.id}.${show.id}`
          const showObjectPurchaseKey = `${eventId}.${showObject.show_id}.${exhibitor.id}`

          newPayment.events[paymentEventKey].shows[paymentShowKey] = {
            ...newPayment.events[paymentEventKey].shows[paymentShowKey],
            ...show,
            payments_exhibitors: {
              ...newPayment.events[paymentEventKey].shows[paymentShowKey]
                ?.payments_exhibitors,
              [showObjectPurchaseKey]: {
                ...newPayment.events[paymentEventKey].shows[paymentShowKey]
                  ?.payments_exhibitors[showObjectPurchaseKey],
                ...paymentsExhibitors,
                exhibitor,
                payments_show_objects: [
                  ...(newPayment.events[paymentEventKey].shows[paymentShowKey]
                    ?.payments_exhibitors[showObjectPurchaseKey]
                    ?.payments_show_objects ?? []),
                  {
                    ...paymentsShowObjects,
                    show_object: showObject,
                  },
                ],
              },
            },
          }
        }
      }
    }

    payments.value.set(payment.id, newPayment)
  }
}

useInfiniteScroll(
  dashboardPanelContent,
  async () => {
    await loadPayments()

    debouncedFormData()
  },
  {
    canLoadMore: () =>
      payments.value.size > 0 && count.value > payments.value.size,
  },
)

watchOnce(
  () => auth.user,
  async (value) => {
    if (!value) {
      return
    }

    await loadPayments()

    debouncedFormData()
  },
  { deep: true, immediate: true },
)

watch(
  [
    () => data.events,
    () => data.shows,
    () => data.payments,
    () => data.payments_exhibitors,
    () => data.payments_extras,
    () => data.payments_show_objects,
    () => data.exhibitors,
    () => data.extras,
    () => data.show_objects,
  ],
  debouncedFormData,
  { deep: true },
)
</script>
